import { z } from 'zod';
import { addDays, isoDate, isoWeekNumber, monthOf, weekOf } from './dates.js';
import { holidayOn, type HolidayRegion } from './holidays.js';
import { isWeekend } from './working-days.js';

// Month and week grid of the project calendar (TASK-10). The calendar is only display; tasks
// appear on their due date.

export const calendarViewSchema = z.enum(['month', 'week']);
export type CalendarView = z.infer<typeof calendarViewSchema>;

export interface CalendarDay {
  date: string;
  /** False for the leading and trailing days of neighbouring months in the month grid. */
  inPeriod: boolean;
  weekend: boolean;
  /** Name of the public holiday in the region, or null. */
  holiday: string | null;
}

export interface CalendarWeek {
  /** ISO week number. */
  number: number;
  days: CalendarDay[];
}

export interface CalendarGrid {
  /** The month or week shown. */
  period: { from: string; to: string };
  /** All days in the grid: full weeks from Monday to Sunday. */
  range: { from: string; to: string };
  weeks: CalendarWeek[];
}

/** The period containing `date`: its month or its ISO week. */
export function calendarPeriod(view: CalendarView, date: string): { from: string; to: string } {
  return view === 'month' ? monthOf(date) : weekOf(date);
}

/** Full weeks (Monday to Sunday) covering the period of `date`, with weekends and holidays. */
export function calendarGrid(
  view: CalendarView,
  date: string,
  region: HolidayRegion,
): CalendarGrid {
  const period = calendarPeriod(view, date);
  const range = { from: weekOf(period.from).from, to: weekOf(period.to).to };
  const weeks: CalendarWeek[] = [];
  for (let monday = range.from; monday <= range.to; monday = addDays(monday, 7)) {
    const days = Array.from({ length: 7 }, (_, i) => {
      const day = addDays(monday, i);
      return {
        date: day,
        inPeriod: day >= period.from && day <= period.to,
        weekend: isWeekend(day),
        holiday: holidayOn(day, region)?.name ?? null,
      };
    });
    weeks.push({ number: isoWeekNumber(monday), days });
  }
  return { period, range, weeks };
}

/**
 * Anchor date of the previous (`step` = -1) or next (+1) period: the first of the month in the
 * month view, the Monday in the week view.
 */
export function shiftCalendar(view: CalendarView, date: string, step: number): string {
  if (view === 'week') return addDays(weekOf(date).from, step * 7);
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  return isoDate(year, month + step, 1); // Date.UTC rolls months over into the next year
}
